import { z } from 'zod';
/**
 * The closed set of failures that may reach the wire (agreement section 7).
 * It is closed on purpose: the partner has written its reaction to each code
 * once, and a code they have not seen has no defined handling.
 */
export const ErrorCode = z.enum([
    'invalid', // 400 — a bug on one side; the partner escalates to the owner
    'unauthenticated', // 401 — key wrong or revoked; the partner alerts immediately
    'forbidden', // 403 — tenant mismatch; never retried
    'not_found', // 404 — no such booking or guest
    'conflict', // 409 — slot taken in between; re-check and offer alternatives
    'rate_limited', // 429 — backoff, honouring Retry-After
    'unavailable', // 500/503 — retry with backoff
    'unsupported', // 501 — this capability does not exist here
]);
const STATUS = {
    invalid: 400,
    unauthenticated: 401,
    forbidden: 403,
    not_found: 404,
    conflict: 409,
    rate_limited: 429,
    unavailable: 503,
    unsupported: 501,
};
export const ErrorBody = z.object({
    error: z.object({
        code: ErrorCode,
        message: z.string(),
        detail: z.record(z.string(), z.unknown()).optional(),
    }),
});
/**
 * The only error a route may raise. One serializer turns it into the single
 * wire shape, which is how the agreement's "never return 200 with an error
 * inside it" stays true without every route having to remember.
 */
export class ApiError extends Error {
    code;
    status;
    detail;
    /** Seconds, for `rate_limited` only. */
    retryAfter;
    constructor(code, message, options = {}) {
        super(message, { cause: options.cause });
        this.name = 'ApiError';
        this.code = code;
        this.status = STATUS[code];
        this.detail = options.detail;
        this.retryAfter = options.retryAfter;
    }
    toBody() {
        return {
            error: { code: this.code, message: this.message, ...(this.detail ? { detail: this.detail } : {}) },
        };
    }
    static invalid(message, detail) {
        return new ApiError('invalid', message, { detail });
    }
    static unauthenticated(message = 'Invalid or revoked API key.') {
        return new ApiError('unauthenticated', message);
    }
    static forbidden(message = 'The tenant does not match this key.') {
        return new ApiError('forbidden', message);
    }
    static notFound(message = 'Not found.', detail) {
        return new ApiError('not_found', message, { detail });
    }
    static conflict(message, detail) {
        return new ApiError('conflict', message, { detail });
    }
    static rateLimited(retryAfter) {
        return new ApiError('rate_limited', 'Too many requests.', { retryAfter });
    }
    static unavailable(message = 'Temporarily unavailable.', cause) {
        return new ApiError('unavailable', message, { cause });
    }
    static unsupported(what) {
        return new ApiError('unsupported', `${what} is not supported by this provider.`);
    }
}
export function isApiError(e) {
    return e instanceof ApiError;
}
//# sourceMappingURL=errors.js.map